<template>
    <div class="space-y-1">
        <Label
            :id="labelId"
            :label="props.label"
            :input-id="triggerId"
            :help-button-text="props.helpButtonText"
            :is-required="props.isRequired"
            :is-disabled="isDisabled"
        />

        <Tooltip :is-teleported="true" class="max-w-full">
            <template #trigger>
                <button
                    :id="triggerId"
                    ref="triggerElement"
                    aria-haspopup="dialog"
                    :aria-expanded="isOpen"
                    :aria-labelledby="`${labelId} ${valueId}`"
                    :aria-describedby="ariaDescribedBy"
                    :aria-disabled="isDisabled || undefined"
                    class="flex flex-row max-w-full items-center gap-2 rounded-md border border-dashed px-1.5 py-1 text-left text-sm focus-visible:outline-2 focus-visible:outline-offset-2"
                    :class="stateClasses"
                    type="button"
                    @click="onClick"
                >
                    <UserReferenceAvatars :users="props.assignedUsers" :mode="props.mode" />
                    <span :id="valueId" ref="valueElement" class="truncate">
                        {{ valueText }}
                    </span>
                    <WarningIcon
                        v-if="hasErrors"
                        class="mr-1 size-5 shrink-0"
                        :class="isDisabled ? 'text-gray-500' : 'text-red-600'"
                        aria-hidden="true"
                    />
                    <span v-if="isDisabled" :id="disabledExplanationId" class="sr-only">
                        {{ props.disabledExplanation }}
                    </span>
                </button>
            </template>
            <template #text>
                <div class="max-w-md text-left">
                    <div :class="{ 'text-center': isTruncated }">
                        {{ isDisabled ? props.disabledExplanation : $t('assign-user-hint') }}
                    </div>
                    <div v-if="isTruncated && names.length" class="mt-1 border-t border-gray-200 pt-1 text-gray-600">
                        {{ valueText }}
                    </div>
                </div>
            </template>
        </Tooltip>

        <p v-if="props.description" :id="descriptionId" class="text-sm text-gray-500">
            {{ props.description }}
        </p>

        <div v-if="hasErrors" :id="errorId" role="alert" class="text-sm text-red-600">
            <p v-for="message in props.errorMessages" :key="message">{{ message }}</p>
        </div>

        <UserReferenceDialog
            v-model:query="query"
            :label-id="labelId"
            :is-open="isOpen"
            :anchor-element="triggerElement"
            :assign-disabled-explanation="assignDisabledExplanation"
            :assigned-count="props.assignedCount"
            :unassigned-count="props.unassignedCount"
            :has-assigned-users="props.assignedUsers.length > 0"
            @close="onClose"
        >
            <template #assigned>
                <slot name="assigned" />
            </template>
            <template #unassigned>
                <slot name="unassigned" />
            </template>
            <template v-if="$slots.actions" #actions>
                <slot name="actions" />
            </template>
        </UserReferenceDialog>
    </div>
</template>

<script setup lang="ts">
import { useElementOverflow } from '@vueuse/core';
import { useFluent } from 'fluent-vue';
import { computed, provide, ref, useId, useTemplateRef } from 'vue';
import Label from '../Form/Label.vue';
import WarningIcon from '../Icon/WarningIcon.vue';
import Tooltip from '../Tooltip/Tooltip.vue';
import { triggerHideTooltipKey } from '../Tooltip/injectionKeys';
import type { UserReferenceUser } from './UserReference.types';
import UserReferenceAvatars from './UserReferenceAvatars.vue';
import UserReferenceDialog from './UserReferenceDialog.vue';
import type { UserReferenceMode } from './UserReferenceStatic.vue';

type UserReferenceProps = {
    label: string;
    mode: UserReferenceMode;
    assignedUsers: UserReferenceUser[];
    disabledExplanation?: string;
    description?: string;
    helpButtonText?: string;
    isRequired?: boolean;
    errorMessages?: string[];
    assignedCount: number;
    unassignedCount: number;
};

const props = defineProps<UserReferenceProps>();
const query = defineModel<string>('query', { default: '' });

const { $t } = useFluent();

const triggerId = `user-reference-${useId()}`;
const labelId = `${triggerId}-label`;
const valueId = `${triggerId}-value`;
const disabledExplanationId = `${triggerId}-disabled`;
const descriptionId = `${triggerId}-info`;
const errorId = `${triggerId}-error`;

const hasErrors = computed(() => (props.errorMessages ?? []).length > 0);
const isDisabled = computed(() => Boolean(props.disabledExplanation));

type TriggerState = 'Disabled' | 'Disabled-With-Errors' | 'Empty' | 'Assigned' | 'With-Errors';

const triggerState = computed<TriggerState>(() => {
    if (isDisabled.value) {
        return hasErrors.value ? 'Disabled-With-Errors' : 'Disabled';
    }
    if (hasErrors.value) {
        return 'With-Errors';
    }
    return props.assignedUsers.length === 0 ? 'Empty' : 'Assigned';
});

const stateClasses = computed(() => {
    switch (triggerState.value) {
        case 'Disabled':
            return 'cursor-not-allowed border-gray-200 bg-gray-50 text-gray-500 focus-visible:outline-gray-400';

        case 'Empty':
            return 'cursor-pointer border-gray-300 bg-gray-100 text-gray-700 focus-visible:outline-gray-700 hover:border-solid hover:border-gray-400 active:border-solid active:border-gray-700 active:bg-gray-200';

        case 'Assigned':
            return 'cursor-pointer border-sky-300 bg-sky-100 text-gray-700 focus-visible:outline-gray-700 hover:border-solid hover:border-sky-400 active:border-solid active:border-sky-600 active:bg-sky-200';

        case 'Disabled-With-Errors':
            return 'cursor-not-allowed border-red-500 bg-red-50 text-red-700 focus-visible:outline-gray-400';

        case 'With-Errors':
            return 'cursor-pointer border-red-500 bg-red-100 text-red-700 focus-visible:outline-gray-700 hover:border-solid hover:border-red-600 active:border-solid active:border-red-700 active:bg-red-200';

        default:
            throw new Error(`Unknown trigger state: ${triggerState.value satisfies never}`);
    }
});

const names = computed(() => props.assignedUsers.map((user) => `${user.firstName} ${user.lastName}`));

const valueText = computed(() => (names.value.length === 0 ? $t('assign-user') : names.value.join(', ')));

const valueElement = useTemplateRef('valueElement');
const { isXOverflowed: isTruncated } = useElementOverflow(valueElement, { observeMutation: true });

const ariaDescribedBy = computed(() => {
    const ids = [
        isDisabled.value ? disabledExplanationId : undefined,
        props.description ? descriptionId : undefined,
        hasErrors.value ? errorId : undefined,
    ].filter(Boolean);

    return ids.length ? ids.join(' ') : undefined;
});

const assignDisabledExplanation = computed(() =>
    props.mode === 'Max-1' && props.assignedUsers.length > 0 ? $t('max-one-user-assigned') : undefined,
);

const triggerElement = useTemplateRef('triggerElement');
const isOpen = ref(false);

const triggerHideTooltipKeyCounter = ref(0);
provide(triggerHideTooltipKey, triggerHideTooltipKeyCounter);

function triggerHideTooltip() {
    triggerHideTooltipKeyCounter.value = triggerHideTooltipKeyCounter.value + 1;
}

function onClick() {
    if (isDisabled.value) {
        return;
    }
    triggerHideTooltip();
    isOpen.value = !isOpen.value;
}

function onClose() {
    isOpen.value = false;
    triggerHideTooltip();
}
</script>
